import {
  useAuth,
  useI18N,
  useSavedPlaces,
  VenueDetailsBySlugResponse,
  useBusinessInterests,
} from "common/hooks";
import { useNearbyBusinesses } from "common/hooks/business/useNearbyBusinesses";
import { bucketUrlPrefix, googleMapKey, googleMapId } from "common/config";
import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  lazy,
  Suspense,
} from "react";
import {
  MapPinIcon,
  GlobeIcon,
  BellIcon,
  ShareNetworkIcon,
  ArrowRightIcon,
  CaretRightIcon,
  WarningIcon,
  HeartStraightIcon,
  BellRingingIcon,
} from "@phosphor-icons/react";
// Lazy load the NearbyVenues component to reduce initial bundle size
const NearbyVenues = lazy(() =>
  import("@/components/WhatsNearby/NearbyVenues").then((module) => ({
    default: module.NearbyVenues,
  })),
);
import Loader from "@/components/common/Loader";
import { RWebShare } from "react-web-share";
import { Venue, BusinessInterestType } from "common/types";
import { getVenueLocationSubtitle, pinTypeForVenueDetails } from "common/utils";
import { getVenueSlugs } from "@/lib/utils";
import InterestNotificationModal from "@/components/Notifications/NotificationModal/InterestNotificationModal";
import { createPageAnalytics, trackEvent } from "common/analytics";
import { entryPageFromCurrentPage } from "@/analytics/entryPage";
import { useAuthModals, AuthModalScreen } from "@/hooks/useAuthModals";
import { useAtom } from "jotai";
import { actionToPerformAfterLoginAtom } from "@/store/atoms";
import { Modal } from "react-responsive-modal";
import CreateList from "@/components/SavedPlaces/CreateList";
import AddRemoveVenueToList from "@/components/SavedPlaces/AddRemoveVenueToList";

type ComplexVenueSheetContentProps = {
  onClose: () => void;
  venueDetailsResponse: VenueDetailsBySlugResponse;
  fullSize?: boolean;
  onRefresh: () => void;
  openAllPhotosInline: boolean;
  onOpenAllPhotos?: () => void;
  cid?: string;
  tid?: string;
  onNotificationClick?: (venue: Venue) => void;
};

function ComplexVenueSheetContent({
  venueDetailsResponse,
  onNotificationClick,
  cid,
  tid,
}: ComplexVenueSheetContentProps) {
  const venueDetails = venueDetailsResponse.details;

  // save_place_action_button -> place_save_cta_activated (fired on the heart tap, before the
  // login/save branch). Same element as the basic venue sheet.
  const trackSavePlaceCta = () => {
    createPageAnalytics("place").track("save_place_action_button", {
      brand_id: venueDetails?.brandId != null ? String(venueDetails?.brandId) : "",
      brand_name: venueDetails?.brandName ?? "",
      business_id: venueDetails?.business?.id?.toString() ?? "",
      entry_page: entryPageFromCurrentPage(),
      pin_type: pinTypeForVenueDetails(venueDetails),
      place_name: venueDetails?.name ?? "",
    });
  };
  const [activeSection, setActiveSection] = useState("overview");
  const [shareOrigin, setShareOrigin] = useState("");
  useEffect(() => setShareOrigin(window.location.origin), []);

  const locationSubtitle = getVenueLocationSubtitle({
    sublocality: venueDetails.sublocality,
    route: venueDetails.route,
    postalTown: venueDetails.postalTown,
    locality: venueDetails.locality,
    cityLocation: venueDetails.cityLocation,
    // businessType omitted: complex venues already show it on the "distance | type" line
  });
  const [isAlertsExpanded, setIsAlertsExpanded] = useState(false);
  const i18n = useI18N();
  const [openSavedPlacesModal, setOpenSavedPlacesModal] = useState(false);
  const [savePlaceListMode, setSavePlaceListMode] = useState("");
  const { user, isLoggedIn } = useAuth();
  const isMapper = !!(user?.roles?.isSociabilityMapper || user?.roles?.isSociabilityTeam);
  const [, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom,
  );
  const { showModal } = useAuthModals();  
  const [listNameCreated, setListNameCreated] = useState("");
  
  // Live region for screen reader announcements
  const [liveMessage, setLiveMessage] = useState("");

  // Notification modal state
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [selectedVenueForNotification, setSelectedVenueForNotification] =
    useState<{
      id: string;
      name: string;
      brandId?: string;
      brandName?: string;
      interestId?: string;
      interestedType?: string;
      notifyAccessibilityInformation: boolean;
      notifyLiveAlert: boolean;
    } | null>(null);

  const handleCreateList = (listNameCreated) => {
    console.log("inside handleCreateList listNameCreated=", listNameCreated);
    setListNameCreated(listNameCreated);
  };

  const {
    refetch,
    isLoading: isSavedPlacesLoading,
    isFetching: isSavedPlacesFetching,
    data,
  } = useSavedPlaces();
  const savedPlaces = data?.saved_places || [];

  // Get business interests to check if venue has notifications enabled
  const { data: businessInterests } = useBusinessInterests(false);

  // CheckIcon if this venue has an interest record
  const venueInterest = useMemo(() => {
    if (!businessInterests || !venueDetails?.business?.id) return undefined;
    return businessInterests.find(
      (interest) => interest.business_id === venueDetails.business.id,
    );
  }, [businessInterests, venueDetails?.business?.id]);

  // CheckIcon if venue has any notifications enabled
  const hasNotification = useMemo(() => {
    if (!venueInterest) return false;
    return (
      venueInterest.notify_accessibility_information === 1 ||
      venueInterest.notify_live_alert === 1
    );
  }, [venueInterest]);

  // Create a Set of venue IDs with notifications enabled (for nearby venues)
  const notificationVenueIds = useMemo(() => {
    if (!businessInterests) return new Set<number>();

    return new Set(
      businessInterests
        .filter(
          (interest) =>
            interest.notify_accessibility_information === 1 ||
            interest.notify_live_alert === 1,
        )
        .map((interest) => interest.business_id),
    );
  }, [businessInterests]);

  // Create a Map for quick lookup of business interest details (for nearby venues)
  const businessInterestMap = useMemo(() => {
    if (!businessInterests) return new Map<number, BusinessInterestType>();

    return new Map<number, BusinessInterestType>(
      businessInterests.map(
        (interest) =>
          [interest.business_id, interest] as [number, BusinessInterestType],
      ),
    );
  }, [businessInterests]);

  // Add nearby businesses query
  const { data: nearbyBusinessesData, isLoading: isLoadingNearbyBusinesses } =
    useNearbyBusinesses(venueDetails.business.id.toString());

  useEffect(() => {
    if (user) {
      refetch();
    }
  }, [refetch, user]);

  const handleSaveVenue = async () => {
    if (!user) {
      console.log("Please login");
      return;
    }

    const { data } = await refetch();
    if (!data) {
      return;
    }

    setOpenSavedPlacesModal(true);

    if (data.saved_places.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  };

  // Handle get alert for nearby venues
  const handleGetAlert = useCallback(
    (venueId: string | number) => {
      if (!isLoggedIn) {
        showModal(AuthModalScreen.EnterEmail);
        return;
      }

      // Get nearby venues from the API response
      const nearbyVenues = nearbyBusinessesData?.data?.nearbyBusinessList || [];
      const venue = nearbyVenues.find((v: any) => v.id === venueId);

      if (venue) {
        const businessId = venue.business?.id;

        // CheckIcon if this venue already has an interest record
        const existingInterest: BusinessInterestType | undefined = businessId
          ? businessInterestMap.get(businessId)
          : undefined;

        setSelectedVenueForNotification({
          id: businessId?.toString() || venueId.toString(),
          name: venue.name,
          brandId: venue.brandId != null ? String(venue.brandId) : "",
          brandName: venue.brandName ?? "",
          interestId: existingInterest?.id.toString(),
          interestedType: existingInterest?.interested_type,
          notifyAccessibilityInformation:
            existingInterest?.notify_accessibility_information === 1,
          notifyLiveAlert: existingInterest?.notify_live_alert === 1,
        });
        setIsNotificationModalOpen(true);
      }
    },
    [isLoggedIn, showModal, nearbyBusinessesData, businessInterestMap],
  );

  const handleNotificationModalClose = useCallback(() => {
    setIsNotificationModalOpen(false);
  }, []);

  // Generate static map URL - optimized with stable fallback
  const staticMapUrl = useMemo(() => {
    const baseUrl = "https://maps.googleapis.com/maps/api/staticmap";

    // Base parameters
    const params = new URLSearchParams({
      zoom: "13",
      size: "510x430",
      maptype: "roadmap",
      key: googleMapKey,
      map_id: googleMapId,
      center: `${venueDetails.latitude},${venueDetails.longitude}`,
    });

    // Main venue marker (blue pin) - always present
    const mainVenueMarker = `icon:https%3A%2F%2Fdevionic.sociability.app%2Fassets%2Fblue_pin.png||${venueDetails.latitude},${venueDetails.longitude}`;

    // Return basic map immediately if nearby data is still loading
    if (
      isLoadingNearbyBusinesses ||
      !nearbyBusinessesData?.data?.nearbyBusinessList
    ) {
      return `${baseUrl}?${params.toString()}&markers=${mainVenueMarker}`;
    }

    // Add nearby venues markers only when data is available
    const nearbyMarkers: string[] = [];
    nearbyBusinessesData.data.nearbyBusinessList
      .slice(0, 10)
      .forEach((venue) => {
        const pinColor = venue.reviewsSubmitted > 0 ? "orange_pin" : "grey_pin";
        const marker = `icon:https%3A%2F%2Fdevionic.sociability.app%2Fassets%2F${pinColor}.png||${venue.latitude},${venue.longitude}`;
        nearbyMarkers.push(marker);
      });

    // Combine all markers
    const allMarkers = [mainVenueMarker, ...nearbyMarkers].join("&markers=");
    return `${baseUrl}?${params.toString()}&markers=${allMarkers}`;
  }, [
    venueDetails.latitude,
    venueDetails.longitude,
    isLoadingNearbyBusinesses,
    nearbyBusinessesData?.data?.nearbyBusinessList,
  ]);

  // Add hasNearbyVenues constant - optimized to show section while loading, hide only if no data
  const hasNearbyVenues = useMemo(() => {
    // Don't show for Basic templates
    if (venueDetails.templateType === "Basic") return false;

    // Show section while loading (data will load progressively)
    if (isLoadingNearbyBusinesses) return true;

    // After loading, only show if there are actual nearby venues
    if (nearbyBusinessesData?.data?.nearbyBusinessList) {
      return nearbyBusinessesData.data.nearbyBusinessList.length > 0;
    }

    // If no data yet but not loading, assume we'll have data (progressive loading)
    return true;
  }, [
    venueDetails.templateType,
    isLoadingNearbyBusinesses,
    nearbyBusinessesData?.data?.nearbyBusinessList,
  ]);

  const hasAccessibilityData = useMemo(() => {
    return (
      (venueDetailsResponse.accesibilityCategories?.length ?? 0) > 0 ||
      (venueDetailsResponse.accesibilityCategoriesHybrid?.length ?? 0) > 0 ||
      (venueDetailsResponse.accesibilityCategoriesEnterprise?.length ?? 0) > 0
    );
  }, [venueDetailsResponse]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      let headerOffset = 80; // Default offset

      const headerElement =
        document.querySelector("header") ||
        document.querySelector("[data-header]");
      if (headerElement) {
        headerOffset = headerElement.getBoundingClientRect().height + 20;
      }

      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      
      // Focus the section heading for keyboard/screen reader orientation
      setTimeout(() => {
        const heading = document.getElementById(`${sectionId}-heading`);
        if (heading) {
          heading.focus();
        }
      }, 300); // Wait for smooth scroll to complete
      
      // sub_nav_tab -> place_content_view_changed (only on an actual section change).
      if (sectionId !== activeSection) {
        trackEvent("place_content_view_changed", {
          brand_id: venueDetails?.brandId != null ? String(venueDetails?.brandId) : "",
          brand_name: venueDetails?.brandName ?? "",
          business_id: venueDetails?.business?.id?.toString() ?? "",
          interaction_method: "tab_tap",
          place_content_type: sectionId,
          place_name: venueDetails?.name ?? "",
          previous_place_content_type: activeSection,
        });
      }
      setActiveSection(sectionId);
    }
  };

  // Optimized scroll spy functionality with debouncing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleScroll = () => {
      // Clear previous timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Debounce scroll events for better performance
      timeoutId = setTimeout(() => {
        const sections = [
          "overview",
          "accessibilityguide",
          "whatsnearby",
          "others",
        ];
        let currentSection = "overview";

        // Calculate header offset once
        let headerOffset = 80;
        const headerElement =
          document.querySelector("header") ||
          document.querySelector("[data-header]");
        if (headerElement) {
          headerOffset = headerElement.getBoundingClientRect().height + 50;
        }

        // Find which section is currently in view
        for (const sectionId of sections) {
          const element = document.getElementById(sectionId);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= headerOffset && rect.bottom > headerOffset) {
              currentSection = sectionId;
              break;
            }
          }
        }

        // Update active section only if it changed
        setActiveSection((prev) =>
          prev !== currentSection ? currentSection : prev,
        );
      }, 100); // 100ms debounce
    };

    // Use passive listener for better performance
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Initial call
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [hasAccessibilityData, hasNearbyVenues, venueDetails.businessTabs]);

  // Generate accessibility cards from cohorts data
  const accessibilityCards = useMemo(() => {
    const { contentSlug } = getVenueSlugs(venueDetails);

    const cohortCards =
      venueDetails.cohortsData
        ?.filter((cohort) => cohort.slug !== contentSlug)
        .map((cohort) => ({
          id: cohort.cohort_id,
          title: cohort.cohort_name,
          spaces: `${cohort.spaces_count} ${i18n.get("spaces")}`,
          image: cohort.visibility_photo
            ? bucketUrlPrefix + cohort.visibility_photo
            : venueDetails.businessPhotoFirst?.path
              ? bucketUrlPrefix + venueDetails.businessPhotoFirst.path
              : undefined,
          link: i18n.get("viewGuideForAllSpaces"),
          slug: cohort.slug,
        })) || [];

    // Calculate total spaces from sync-spaces API (levelOptions)
    // Only count spaces that have accessibility info or photos
    const hasSpaceContent = (space: any) =>
      (space.level_option_titles &&
        Array.isArray(space.level_option_titles) &&
        space.level_option_titles.length > 0) ||
      (space.level_photo_count && space.level_photo_count > 0);

    const totalSpaces =
      venueDetailsResponse.accesibilityCategoriesEnterprise?.reduce(
        (total, category) => {
          return (
            total +
            (category.categoryOptions?.reduce((catTotal, categoryOption) => {
              return (
                catTotal +
                (categoryOption.pageChildrenCategories?.reduce(
                  (childTotal, childCategory) => {
                    return (
                      childTotal +
                      (childCategory.levelOptions?.filter(hasSpaceContent)
                        .length || 0)
                    );
                  },
                  0,
                ) || 0)
              );
            }, 0) || 0)
          );
        },
        0,
      ) || 0;

    // Add default "Full accessibility guide" card at the beginning.
    const defaultCard = {
      id: "full-guide",
      title: i18n.get("accessibilityGuide"),
      spaces: `${totalSpaces} ${i18n.get("spaces")}`,
      image: venueDetails.businessPhotoFirst?.path
        ? bucketUrlPrefix + venueDetails.businessPhotoFirst.path
        : undefined,
      link: i18n.get("viewGuideForAllSpaces"),
      slug: contentSlug,
    };

    return [defaultCard, ...cohortCards];
  }, [
    venueDetails,
    venueDetailsResponse.accesibilityCategoriesEnterprise,
    i18n,
  ]);

  // Generate others cards from businessTabs data
  const othersCards = useMemo(() => {
    return (
      venueDetails.businessTabs?.map((tab) => {
        const firstBlock = tab.blocks?.[0];
        const firstBlockImage = firstBlock?.images?.[0];
        const firstBlockSnippet = firstBlock?.content
          ? firstBlock.content.replace(/<[^>]*>/g, "").substring(0, 150) + "..."
          : "";

        return {
          id: tab.id,
          title: tab.title,
          subtitle:
            tab.subtitle || tab.description || firstBlockSnippet || "",
          image: tab.cover_photo
            ? bucketUrlPrefix + tab.cover_photo
            : firstBlockImage
              ? bucketUrlPrefix + firstBlockImage.image_path
              : venueDetails.businessPhotoFirst?.path
                ? bucketUrlPrefix + venueDetails.businessPhotoFirst.path
                : undefined,
        };
      }) || []
    );
  }, [venueDetails.businessTabs, venueDetails.businessPhotoFirst]);

  // Collect all space IDs visible in the current custom view from review_tags
  const visibleSpaceIds = useMemo(() => {
    const ids = new Set<number>();
    const collect = (categories: typeof venueDetailsResponse.review_tags) => {
      categories?.forEach((category: any) => {
        category.categoryOptions?.forEach((option: any) => {
          option.pageChildrenCategories?.forEach((subCategory: any) => {
            subCategory.levelOptions?.forEach((space: any) => {
              if (space.id != null) ids.add(space.id as number);
            });
            if (subCategory.categoryOptions) collect([subCategory]);
          });
        });
      });
    };
    collect(venueDetailsResponse.review_tags);
    return ids;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueDetailsResponse]);

  // Generate accessibility alerts from businessNotesAll, filtered to visible spaces
  const accessibilityAlerts = useMemo(() => {
    return (
      venueDetails.businessNotesAll
        ?.filter((note) => {
          if (note.status !== "Open") return false;
          // Whole-venue alerts always show; space-specific only if space is in view
          if (note.location_type === "Business") return true;
          return visibleSpaceIds.size === 0 || visibleSpaceIds.has(note.location_id);
        })
        .map((note) => {
          const roles = note.business_note_by_user_roles;
          const authorName = isMapper
            ? note.business_note_by_user
            : roles?.isSociabilityMapper || roles?.isSociabilityTeam
              ? "Sociability team"
              : roles
                ? note.business_note_by_user
                : "Sociability team";
          return {
            id: note.business_note_text_id,
            dateRange:
              note.formatedScheduleDateFrom && note.formatedScheduleDateTo
                ? `${note.formatedScheduleDateFrom} to ${note.formatedScheduleDateTo}`
                : "",
            flaggedDate: `Added on ${note.formatedCreatedAt} by ${authorName}`,
            title: note.business_note_title,
            location: note.location_name,
            description: note.business_note_text,
          };
        }) || []
    );
  }, [venueDetails.businessNotesAll, isMapper, visibleSpaceIds]);

  const handleShare = (venueDetails: Venue) => {
    console.log("shared successfully!");
    // share_place_action_button -> place_share_cta_activated.
    createPageAnalytics("place").track("share_place_action_button", {
      brand_id: venueDetails?.brandId != null ? String(venueDetails?.brandId) : "",
      brand_name: venueDetails?.brandName ?? "",
      business_id: venueDetails?.business?.id?.toString() ?? "",
      place_name: venueDetails?.name ?? "",
    });
  };

  const isVenueSaved = useCallback(() => {
    if (isSavedPlacesFetching || isSavedPlacesLoading) {
      return false;
    }
    if (savedPlaces) {
      const flattened = savedPlaces
        ?.map((e) => e.saved_place_details.map((e) => e.business.id))
        .flat();
      const result = flattened.some((id) => id === venueDetails?.business.id);
      return result;
    }
    return false;
  }, [
    isSavedPlacesFetching,
    isSavedPlacesLoading,
    savedPlaces,
    venueDetails?.business.id,
  ]);

  useEffect(() => {
    if (!isSavedPlacesFetching && !isSavedPlacesLoading) {
      isVenueSaved();
    }
  }, [isSavedPlacesFetching, isSavedPlacesLoading, isVenueSaved]);

  const handleSavedPlacesModalClose = () => {
    console.log("Closing Modal handleSavedPlacesModalClose");
    setOpenSavedPlacesModal(false);
    if (savePlaceListMode === "create") {
      //refetch the lists and open the modal with create button and all lists
      handleSaveVenue();
    } else {
      setListNameCreated("");
    }
  };

  const handleCreateNewClick = () => {
    console.log("handleCreateNewClick");
    setOpenSavedPlacesModal(false);
    setSavePlaceListMode("create");
    setOpenSavedPlacesModal(true);
  };

  return (
    <>
      {hasAccessibilityData && (
        <>
          <main className="min-h-screen w-full bg-bg-brand-secondary-default">
            {/* Hero Banner */}
            <div className="relative w-full h-[280px] md:h-[320px] min-w-full">
              {/* Background Image */}
              <div className="absolute inset-0 overflow-hidden w-full">
                {venueDetails.business?.coverPhoto && (
                  <img
                    alt={
                      venueDetails.business.coverPhotoAltText ||
                      `${venueDetails.name} - Hero Image`
                    }
                    className="w-full h-full object-cover min-w-full"
                    src={bucketUrlPrefix + venueDetails.business.coverPhoto}
                    style={{
                      objectPosition:
                        venueDetails.business.coverPhotoFocalX != null &&
                        venueDetails.business.coverPhotoFocalY != null
                          ? `${venueDetails.business.coverPhotoFocalX}% ${venueDetails.business.coverPhotoFocalY}%`
                          : undefined,
                    }}
                  />
                )}
                {/* Gradient overlay for mobile readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent md:hidden"></div>
              </div>

              {/* Mobile Only - Top Title with Better Readability */}
              <div className="absolute top-0 left-0 right-0 z-20 md:hidden">
                <div className="mx-auto px-4 pt-6">
                  <div className="bg-[color-mix(in_srgb,var(--surface-primary)_95%,transparent)] backdrop-blur-sm rounded-lg px-4 py-3 shadow-lg border border-border-token-neutral-minimal flex flex-col gap-1">
                    <h1 className="title-s-semibold md:title-m-bold tracking-[-0.1px] md:tracking-[-0.16px] !leading-none text-fg-neutral-strong">
                      {venueDetails.name}
                    </h1>
                    {locationSubtitle && (
                      <p className="label-s-medium text-fg-neutral-subtle">
                        {locationSubtitle}
                      </p>
                    )}
                    <p className="body-s-regular text-fg-neutral-strong">
                      {venueDetails.distance} mile | {venueDetails.businessType}
                    </p>
                    {venueDetails.businessVerified != null && (
                      <div className="flex items-center gap-1">
                        <img src="/images/SealCheck.svg" alt="" aria-hidden="true" className="w-5 h-5 shrink-0" />
                        <span className="label-m-semibold text-fg-neutral-subtle whitespace-nowrap">
                          {venueDetails.businessVerified}
                        </span>
                        {venueDetails.businessVerifiedDatetime && (
                          <span className="label-s-medium text-fg-neutral-subtle whitespace-nowrap">
                            {`: ${new Date(venueDetails.businessVerifiedDatetime.replace(" ", "T")).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}`}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Desktop - Content Container (Original Layout) */}
              <div className="hidden md:block absolute bottom-0 left-0 right-0 h-[120px] bg-bg-brand-secondary-default w-full">
                <div className="max-w-[1440px] mx-auto px-6 h-full flex items-end gap-6 relative w-full">
                  {/* Profile Image */}
                  <div className="absolute -top-[60px] left-6">
                    <div className="w-[140px] h-[140px] rounded-full border-4 border-white bg-white overflow-hidden">
                      <img
                        alt={
                          venueDetails.business?.themeLogoAltText ||
                          venueDetails.name
                        }
                        className="w-full h-full object-cover"
                        src={
                          venueDetails.business?.themeLogo
                            ? bucketUrlPrefix + venueDetails.business.themeLogo
                            : venueDetails.businessPhotoFirst?.path
                              ? bucketUrlPrefix +
                                venueDetails.businessPhotoFirst.path
                              : "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=140&h=140&fit=crop&crop=center"
                        }
                        style={{
                          objectPosition:
                            venueDetails.business?.themeLogo &&
                            venueDetails.business.themeLogoFocalX != null &&
                            venueDetails.business.themeLogoFocalY != null
                              ? `${venueDetails.business.themeLogoFocalX}% ${venueDetails.business.themeLogoFocalY}%`
                              : undefined,
                        }}
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 ml-[164px] pb-6 pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 flex flex-col gap-1">
                        <div className="title-m-bold tracking-[-0.16px] !leading-none" aria-hidden="true">
                          {venueDetails.name}
                        </div>
                        {locationSubtitle && (
                          <p className="label-s-medium text-fg-neutral-subtle">
                            {locationSubtitle}
                          </p>
                        )}
                        <p className="body-s-regular">
                          {venueDetails.distance} mile |{" "}
                          {venueDetails.businessType}
                        </p>
                        {venueDetails.businessVerified != null && (
                          <div className="flex items-center gap-1">
                            <img src="/images/SealCheck.svg" alt="" aria-hidden="true" className="w-5 h-5 shrink-0" />
                            <span className="label-m-semibold text-fg-neutral-subtle whitespace-nowrap">
                              {venueDetails.businessVerified}
                            </span>
                            {venueDetails.businessVerifiedDatetime && (
                              <span className="label-s-medium text-fg-neutral-subtle whitespace-nowrap">
                                {`: ${new Date(venueDetails.businessVerifiedDatetime.replace(" ", "T")).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}`}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Desktop Action Buttons */}
                      <div className="flex gap-3 items-center ml-4">       


                        
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onNotificationClick?.(
                              venueDetails as unknown as Venue,
                            );
                          }}
                          aria-label={
                            hasNotification
                              ? `Notifications enabled for ${venueDetails.name}`
                              : `Enable notifications for ${venueDetails.name}`
                          }
                          className="
                          px-4 py-2.5 mt-2 mb-2 flex items-center justify-center gap-2 w-full h-11 
                          bg-bg-brand-secondary-default 
                          text-fg-neutral-strong 
                          border border-border-token-brand-minimal 
                          hover:bg-bg-brand-secondary-hover
                          active:bg-bg-brand-secondary-active
                          focus-blue
                          rounded-[8px]
                          transition-all duration-200                       
                          min-h-[44px]"
                        >
                          {hasNotification ? (
                            <BellRingingIcon size={16} className="text-fg-neutral-strong" weight="fill" />
                          ) : (
                            <BellIcon size={16} className="text-fg-neutral-strong" weight="regular" />
                          )}

                          <span className="label-m-medium text-fg-neutral-strong">{i18n.get("notification")}</span>
                        </button>



                        <button
                          type="button"
                          onClick={() => {
                            trackSavePlaceCta();
                            if (isLoggedIn) {
                              handleSaveVenue();
                            } else {
                              // Set the method in the Atom
                              console.log(
                                "setActionToPerformAfterLogin in the Atom",
                              );
                              setActionToPerformAfterLogin("handleSaveVenue");

                              // Show login modal
                              showModal(AuthModalScreen.EnterEmail);
                            }
                          }}
                          aria-label={
                            isVenueSaved()
                              ? `Remove ${venueDetails.name} from saved places`
                              : `Save ${venueDetails.name} to your places`
                          }
                          aria-pressed={isVenueSaved()}
                          className="
                          px-4 py-2.5 mt-2 mb-2 flex items-center justify-center gap-2 w-full h-11 
                          bg-bg-brand-secondary-default 
                          text-fg-neutral-strong 
                          border border-border-token-brand-minimal 
                          hover:bg-bg-brand-secondary-hover
                          active:bg-bg-brand-secondary-active
                          focus-blue
                          rounded-[8px]
                          transition-all duration-200                       
                          min-h-[44px]"
                        >
                          <HeartStraightIcon
                            size={16}
                            weight={isVenueSaved() ? "fill" : "regular"}
                            aria-hidden="true"
                            className="text-fg-neutral-strong"
                          />

                          <span className="label-m-medium text-fg-neutral-strong">{isVenueSaved() ? i18n.get("saved") : i18n.get("save")}</span>
                        </button>




                        

                        {/* Share Button */}
                        <RWebShare
                          data={{
                            text: i18n.get("shareThisVenue"),
                            url:
                              shareOrigin +
                              "/venuedetails/" +
                              venueDetails.business.slug,
                            title: venueDetails.name,
                          }}
                          onClick={() => {
                            handleShare(venueDetails as unknown as Venue);
                          }}
                        >

                          <button
                            type="button"
                            aria-label={`Share ${venueDetails.name}`}
                            className="px-4 py-2.5 flex mt-2 mb-2 flex items-center justify-center gap-2 w-full h-11 bg-bg-brand-primary-default
                    rounded-lg label-m-semibold hover:bg-bg-brand-primary-hover active:bg-bg-brand-primary-active
                    transition-all duration-200 focus-blue
                    min-h-[44px]"
                          >
                            <ShareNetworkIcon
                              size={16}
                              weight="regular"
                              aria-hidden="true"
                              className="text-fg-neutral-onFilled"
                            />
                            <span className="label-m-medium text-fg-neutral-onFilled">{i18n.get("share")}</span>
                          </button>

                        </RWebShare>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile - Profile Image and Action Buttons */}
              <div className="md:hidden absolute bottom-[-40px] left-4 right-4 z-20 flex items-end justify-between">
                {/* Profile Image */}
                <div className="w-[100px] h-[100px] rounded-full border-4 border-white bg-white overflow-hidden shadow-lg">
                  <img
                    alt={
                      venueDetails.business?.themeLogoAltText ||
                      venueDetails.name
                    }
                    className="w-full h-full object-cover"
                    src={
                      venueDetails.business?.themeLogo
                        ? bucketUrlPrefix + venueDetails.business.themeLogo
                        : venueDetails.businessPhotoFirst?.path
                          ? bucketUrlPrefix + venueDetails.businessPhotoFirst.path
                          : "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=140&h=140&fit=crop&crop=center"
                    }
                    style={{
                      objectPosition:
                        venueDetails.business?.themeLogo &&
                        venueDetails.business.themeLogoFocalX != null &&
                        venueDetails.business.themeLogoFocalY != null
                          ? `${venueDetails.business.themeLogoFocalX}% ${venueDetails.business.themeLogoFocalY}%`
                          : undefined,
                    }}
                  />
                </div>

                {/* Mobile Action Buttons - Right side of profile image */}

                <div className="flex gap-2 items-center mb-0 ml-4">
                  
                  <button
                    type="button"
                    className=" rounded-lg p-2 bg-bg-brand-secondary-default
                    hover:bg-bg-brand-ghost-hover 
                    active:bg-bg-brand-ghost-active 
                    transition-colors border border-border-token-brand-minimal focus-blue"
                    onClick={(e) => {
                      e.stopPropagation();
                      onNotificationClick?.(venueDetails as unknown as Venue);
                    }}
                    aria-label={
                      hasNotification
                        ? `Notifications enabled for ${venueDetails.name}`
                        : `Enable notifications for ${venueDetails.name}`
                    }
                  >
                    {hasNotification ? (
                      <BellRingingIcon size={16} className="text-fg-neutral-strong" weight="fill" />
                    ) : (
                      <BellIcon size={16} className="text-fg-neutral-strong" weight="regular" />
                    )}
                  </button>

                  <button
                    type="button"
                    className=" rounded-lg p-2 bg-bg-brand-secondary-default
                    hover:bg-bg-brand-ghost-hover 
                    active:bg-bg-brand-ghost-active 
                    transition-colors border border-border-token-brand-minimal focus-blue"
                    onClick={(e) => {
                      e.stopPropagation();
                      trackSavePlaceCta();
                      if (isLoggedIn) {
                        handleSaveVenue();
                      } else {
                        setActionToPerformAfterLogin("handleSaveVenue");
                        showModal(AuthModalScreen.EnterEmail);
                      }
                    }}
                    aria-label={
                      isVenueSaved()
                        ? `Remove ${venueDetails.name} from saved places`
                        : `Save ${venueDetails.name} to your places`
                    }
                    aria-pressed={isVenueSaved()}
                  >
                    <HeartStraightIcon
                      size={16}
                      weight={isVenueSaved() ? "fill" : "regular"}
                      aria-hidden="true"
                      className="text-fg-neutral-strong"
                    />
                  </button>

                  <RWebShare
                    data={{
                      text: i18n.get("shareThisVenue"),
                      url:
                        shareOrigin +
                        "/venuedetails/" +
                        venueDetails.business.slug,
                      title: venueDetails.name,
                    }}
                    onClick={() => {
                      handleShare(venueDetails as unknown as Venue);
                    }}
                  >
                    <button
                      type="button"
                      className="bg-[#352fc3] hover:bg-[#2d26a8] rounded-lg p-2 shadow-sm transition-colors focus-blue"
                      aria-label={`Share ${venueDetails.name}`}
                    >
                      <ShareNetworkIcon
                        size={16}
                        weight="regular"
                        aria-hidden="true"
                        className="text-fg-neutral-onFilled"
                      />
                    </button>
                  </RWebShare>
                </div>
              </div>
            </div>

            {/* Mobile Spacer for profile image overflow */}
            <div className="md:hidden h-[50px] bg-bg-brand-secondary-default"></div>

            {/* Screen reader announcements */}
            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="sr-only"
            >
              {liveMessage}
            </div>

            {/* Main Content */}
            <div className="bg-bg-brand-secondary-default max-w-[1440px] mx-auto px-4 md:px-6 relative pb-8 md:pb-12">
              <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 pt-6 lg:pt-8">
                {/* Mobile/Tablet Navigation */}
                <nav className="lg:hidden" aria-label="Venue sections">
                  <div className="flex overflow-x-auto gap-2 pb-4 scrollbar-hide">
                    {[
                      { key: "overview", label: i18n.get("overview") },
                      { key: "accessibilityguide", label: i18n.get("accessibilityGuide") },
                      { key: "whatsnearby", label: i18n.get("whatsNearby") },
                      { key: "others", label: i18n.get("others") },
                    ]
                      .filter((section) => {
                        if (section.key === "others" && othersCards.length === 0)
                          return false;
                        if (
                          section.key === "accessibilityguide" &&
                          !hasAccessibilityData
                        )
                          return false;
                        if (section.key === "whatsnearby" && !hasNearbyVenues)
                          return false;
                        return true;
                      })
                      .map((section) => {
                        return (
                          <button
                            key={section.key}
                            type="button"
                            onClick={() => {
                              scrollToSection(section.key);
                            }}
                            aria-current={activeSection === section.key ? "true" : undefined}
                            className={`flex-shrink-0 px-4 py-2 rounded-full label-m-medium font-medium transition-colors focus-blue ${
                              activeSection === section.key
                                ? "bg-bg-brand-primary-default text-white"
                                : "bg-container-brand-minimal text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            {section.label}
                          </button>
                        );
                      })}
                  </div>
                </nav>

                {/* Desktop Left Sidebar Navigation */}
                <nav className="hidden lg:block w-[290px] flex-shrink-0" aria-label="Venue sections">
                  <div className="sticky top-8 pt-8 relative">
                    {/* Continuous vertical line */}
                    <div className="absolute left-0 top-8 bottom-0 w-[2px] bg-[#deddf7]"></div>

                    {[
                      { key: "overview", label: i18n.get("overview") },
                      { key: "accessibilityguide", label: i18n.get("accessibilityGuide") },
                      { key: "whatsnearby", label: i18n.get("whatsNearby") },
                      { key: "others", label: i18n.get("others") },
                    ]
                      .filter((section) => {
                        if (section.key === "others" && othersCards.length === 0)
                          return false;
                        if (
                          section.key === "accessibilityguide" &&
                          !hasAccessibilityData
                        )
                          return false;
                        if (section.key === "whatsnearby" && !hasNearbyVenues)
                          return false;
                        return true;
                      })
                      .map((section) => {
                        return (
                          <button
                            key={section.key}
                            type="button"
                            onClick={() => {
                              scrollToSection(section.key);
                            }}
                            aria-current={activeSection === section.key ? "true" : undefined}
                            className={`w-full text-left pl-4 pr-4 py-3 mb-2 transition-colors flex items-center justify-between relative focus-blue ${
                              activeSection === section.key
                                ? "text-fg-brand-subtle"
                                : "text-fg-neutral-strong hover:text-fg-brand-subtle"
                            }`}
                          >
                            {/* Active indicator line */}
                            {activeSection === section.key && (
                              <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-bg-brand-primary-default"></div>
                            )}

                            <span className="label-m-medium">{section.label}</span>
                            <CaretRightIcon
                              size={16}
                              weight="bold"
                              aria-hidden="true"
                              className={`${
                                activeSection === section.key
                                  ? "text-fg-brand-subtle"
                                  : ""
                              }`}
                            />
                          </button>
                        );
                      })}
                  </div>
                </nav>

                {/* Main Content Area */}
                <div className="flex-1 lg:w-[870px]">
                  <div className="space-y-8 lg:space-y-12">
                    {/* Overview Section */}
                    <section id="overview" aria-labelledby="overview-heading" className="space-y-4 md:space-y-6">
                      <h2 id="overview-heading" tabIndex={-1} className="title-s-semibold text-fg-neutral-strong tracking-[-0.11px] md:tracking-[-0.13px]">
                        {i18n.get("overview")}
                      </h2>
                      <p className="label-m-medium text-fg-neutral-strong">
                        {venueDetails.business.description}
                      </p>
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <MapPinIcon size={24} aria-hidden="true" className="flex-shrink-0" />
                          <p className="label-m-medium text-fg-neutral-strong">
                            {venueDetails.address}
                          </p>
                        </div>
                        {venueDetails.website && (
                          <div className="flex items-center gap-4">
                            <GlobeIcon size={24} aria-hidden="true" className="flex-shrink-0" />
                            <a
                              href={venueDetails.website.startsWith('http') ? venueDetails.website : `https://${venueDetails.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => {
                                // client_webpage_link_link_button -> external_link_cta_activated.
                                trackEvent("external_link_cta_activated", {
                                  link_destination: "website",
                                });
                              }}
                              className="label-m-medium text-fg-brand-subtle hover:text-fg-brand-strong underline focus-blue"
                            >
                              {venueDetails.website}
                            </a>
                          </div>
                        )}
                      </div>
                    </section>

                    {/* Accessibility Guide Section */}
                    {hasAccessibilityData && (
                      <section
                        id="accessibilityguide"
                        aria-labelledby="accessibilityguide-heading"
                        className="space-y-4 md:space-y-6"
                      >
                        <h2 id="accessibilityguide-heading" tabIndex={-1} className="title-s-semibold text-fg-neutral-strong tracking-[-0.11px] md:tracking-[-0.13px]">
                          {i18n.get("accessibilityGuide")}
                        </h2>

                        {/* Accessibility Alerts - Only show if there are alerts */}
                        {accessibilityAlerts.length > 0 && (
                          <section aria-labelledby="alerts-heading">
                            <div className="bg-bg-error-tertiary-default rounded-lg border border-border-token-error-minimal hover:bg-bg-error-tertiary-hover">
                              <button
                                type="button"
                                onClick={() => {
                                  setIsAlertsExpanded(!isAlertsExpanded)
                                }}
                                className="w-full flex items-center justify-between p-3 hover:bg-error-100 transition-colors rounded-lg focus-blue"
                                aria-expanded={isAlertsExpanded}
                                aria-controls="alerts-list"
                              >
                                <div className="flex items-center gap-3">
                                  <WarningIcon
                                    size={24}
                                    weight="fill"
                                    aria-hidden="true"
                                    className="text-fg-error-subtle"
                                  />
                                  <h3 id="alerts-heading" className="label-m-semibold text-fg-neutral-strong">
                                    {i18n.get("accessibilityAlerts")}
                                  </h3>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="label-s-medium text-fg-neutral-strong">
                                    {accessibilityAlerts.length}
                                  </span>
                                  <CaretRightIcon
                                    size={16}
                                    aria-hidden="true"
                                    weight="bold"
                                    className={`text-fg-neutral-strong transition-transform ${
                                      isAlertsExpanded ? "rotate-90" : ""
                                    }`}
                                  />
                                </div>
                              </button>

                            {/* Expanded Alerts — capped at 5 */}
                            {isAlertsExpanded && (
                              <>
                                <ul id="alerts-list" className="px-3 pb-3 space-y-3">
                                  {accessibilityAlerts.slice(0, 5).map((alert) => (
                                    <li key={alert.id}>
                                      <article className="bg-bg-brand-secondary-default rounded-lg p-4">
                                        <div className="mb-2">
                                          <p className="label-s-medium">
                                            {alert.dateRange && (
                                              <>
                                                <span className="text-blue-600">
                                                  {alert.dateRange}
                                                </span>
                                                <span> • </span>
                                              </>
                                            )}
                                            <span className="text-fg-neutral-strong">
                                              {alert.flaggedDate}
                                            </span>
                                          </p>
                                        </div>
                                        {alert.title && (
                                          <h4 className="label-m-semibold text-fg-neutral-strong mb-2">
                                            {alert.title}
                                          </h4>
                                        )}
                                        {alert.location && (
                                          <div className="flex items-center gap-1 mb-2">
                                            <MapPinIcon
                                              size={14}
                                              aria-hidden="true"
                                              className="text-gray-500"
                                            />
                                            <span className="label-s-regular text-gray-600">
                                              {alert.location}
                                            </span>
                                          </div>
                                        )}
                                        {alert.description && (
                                          <p className="label-s-regular text-fg-neutral-strong">
                                            {alert.description}
                                          </p>
                                        )}
                                      </article>
                                    </li>
                                  ))}
                                </ul>
                                {/* View all alerts link */}
                                <li className="list-none px-3 pb-3">
                                  <a
                                    href={(() => {
                                      const { contentSlug } = getVenueSlugs(venueDetails);
                                      const params = new URLSearchParams({ tab: "alerts", alertsTab: "public" });
                                      if (cid) params.set("cid", cid);
                                      if (tid) params.set("tid", tid);
                                      return `/accessibility-guide/${contentSlug}?${params.toString()}`;
                                    })()}
                                    onClick={() => {
                                      // accessibility_alerts_banner_link_button -> accessibility_alerts_viewed
                                      createPageAnalytics("place").track(
                                        "accessibility_alerts_banner_link_button",
                                        {
                                          alerts_count: accessibilityAlerts.length,
                                          business_id:
                                            venueDetails?.business?.id != null
                                              ? String(venueDetails.business.id)
                                              : "",
                                          place_header_name: "",
                                          place_name: venueDetails?.name ?? "",
                                          place_space_name: "",
                                        },
                                      );
                                    }}
                                    className="flex items-center justify-center gap-2 px-4 py-3 bg-bg-brand-secondary-default rounded-lg label-m-medium text-fg-brand-subtle hover:text-fg-brand-strong hover:bg-container-neutral-minimal transition-colors focus-blue"
                                  >
                                    View all {accessibilityAlerts.length} alerts
                                    <ArrowRightIcon size={16} aria-hidden="true" />
                                  </a>
                                </li>
                              </>
                            )}
                            </div>
                          </section>
                        )}

                        {/* Accessibility Cards Grid */}
                        {accessibilityCards.length === 1 && (
                          <>
                            {accessibilityCards.map((card) => (
                              <>
                                {/* Mobile Layout */}
                                <div className="md:hidden space-y-4">
                                  <div className="w-full h-[200px] rounded-lg overflow-hidden">
                                    <img
                                      alt={card.title}
                                      className="w-full h-full object-cover"
                                      src={card.image}
                                    />
                                  </div>
                                  <div className="border border-[#deddf7] rounded-lg p-6">
                                    <h3 className="title-s-semibold text-fg-neutral-strong mb-3">
                                      {card.title}
                                    </h3>
                                    <p className="label-m-medium text-fg-neutral-strong mb-4">
                                      {card.spaces}
                                    </p>
                                    <a
                                      href={`/accessibility-guide/${card.slug}`}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        // Navigate to the accessibility guide page
                                        window.location.href = `/accessibility-guide/${card.slug}`;
                                      }}
                                      className="flex items-center justify-start gap-2 text-fg-brand-subtle hover:text-fg-brand-strong transition-colors focus-blue rounded-lg"
                                    >
                                      <span className="label-m-semibold">
                                        {card.link}
                                      </span>
                                      <ArrowRightIcon size={20} aria-hidden="true" />
                                    </a>
                                  </div>
                                </div>

                                {/* Desktop/Tablet Layout */}
                                <div className="hidden md:block">
                                  <div key={card.id} className="relative flex">
                                    <div className="absolute top-6 left-0 z-10">
                                      <div className="w-[140px] lg:w-[180px] h-[140px] lg:h-[180px] rounded-lg overflow-hidden">
                                        <img
                                          alt={card.title}
                                          className="w-full h-full object-cover"
                                          src={card.image}
                                        />
                                      </div>
                                    </div>
                                    <div className="border border-[#deddf7] rounded-lg pl-28 lg:pl-36 pr-6 py-8 lg:py-12 ml-16 lg:ml-20 flex-1 min-h-[180px] lg:min-h-[228px] flex flex-col justify-center">
                                      <h3 className="title-s-semibold lg:title-s-semibold text-fg-neutral-strong mb-3">
                                        {card.title}
                                      </h3>
                                      <p className="label-m-semibold text-fg-neutral-strong mb-4">
                                        {card.spaces}
                                      </p>
                                        <a
                                          href={`/accessibility-guide/${card.slug}`}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            // Navigate to the accessibility guide page
                                            window.location.href = `/accessibility-guide/${card.slug}`;
                                          }}
                                          className="flex items-center justify-start gap-2 text-fg-brand-subtle hover:text-fg-brand-strong transition-colors focus-blue rounded-lg"
                                        >
                                          <span className="label-m-semibold">
                                            {card.link}
                                          </span>
                                          <ArrowRightIcon
                                            size={18}
                                            aria-hidden="true"
                                            className="lg:w-5 lg:h-5"
                                          />
                                        </a>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ))}
                          </>
                        )}

                        {accessibilityCards.length > 1 && (
                          <div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2">
                            {accessibilityCards.map((card) => (
                              <div key={card.id} className="relative mt-8">
                                {/* Image positioned to overflow outside the top of the container */}
                                <div className="absolute top-[-20px] left-6 z-10">
                                  <div className="w-[100px] h-[100px] rounded-lg overflow-hidden">
                                    <img
                                      alt={card.title}
                                      className="w-full h-full object-cover"
                                      src={card.image}
                                    />
                                  </div>
                                </div>

                                {/* Card container */}
                                <div className="border border-[#deddf7] rounded-lg px-4 pb-3 pt-10 relative">
                                  {/* Content positioned to the right of image */}
                                  <div className="ml-[120px] min-h-[50px] flex flex-col justify-between">
                                    <div>
                                      <h3 className="title-s-semibold text-fg-neutral-strong mb-1">
                                        {card.title}
                                      </h3>
                                      <p className="label-s-regular text-fg-neutral-strong">
                                        {card.spaces}
                                      </p>
                                    </div>
                                  </div>

                                  {/* Divider and link at bottom */}
                                    <div className="mt-4">
                                      <div className="h-[1px] bg-[#deddf7] mb-2"></div>
                                      <a
                                        href={`/accessibility-guide/${card.slug}`}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          // Navigate to the accessibility guide page
                                          window.location.href = `/accessibility-guide/${card.slug}`;
                                        }}
                                        className="w-full flex items-center justify-center gap-2 text-fg-brand-subtle hover:text-fg-brand-strong transition-colors focus-blue rounded-lg"
                                      >
                                        <span className="label-s-medium">
                                          {card.link}
                                        </span>
                                        <ArrowRightIcon size={16} aria-hidden="true" />
                                      </a>
                                    </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </section>
                    )}

                    {/* What's Nearby Section */}
                    {hasAccessibilityData && hasNearbyVenues && (
                      <section id="whatsnearby" aria-labelledby="whatsnearby-heading" className="space-y-4 md:space-y-6">
                        <h2 id="whatsnearby-heading" tabIndex={-1} className="title-s-semibold text-fg-neutral-strong tracking-[-0.11px] md:tracking-[-0.13px]">
                          {i18n.get("whatsNearby")}
                        </h2>

                        {/* Mobile Layout */}
                        <div className="md:hidden space-y-4">
                          <div className="w-full h-[200px] rounded-lg overflow-hidden">
                            {isLoadingNearbyBusinesses ? (
                              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                <div className="animate-pulse">
                                  <div className="w-full h-full bg-gray-300 rounded-lg"></div>
                                </div>
                              </div>
                            ) : (
                              <img
                                alt={`Map showing ${venueDetails.name} and ${nearbyBusinessesData?.data?.nearbyBusinessList?.length || 0} nearby venues`}
                                className="w-full h-full object-cover"
                                src={staticMapUrl}
                                loading="lazy"
                              />
                            )}
                          </div>
                          <div className="border border-[#deddf7] rounded-lg p-6">
                            <h3 className="title-s-semibold text-fg-neutral-strong mb-3">
                              {i18n.get("nearbyVenues")}
                            </h3>
                            <p className="label-m-medium text-fg-neutral-strong mb-4">
                              {i18n.get("fromDiningToLeisure")} {venueDetails.name} {i18n.get("nearbyVenuesDesc")}
                            </p>
                            <a
                              href={`/whats-nearby/${getVenueSlugs(venueDetails).contentSlug}`}
                              onClick={(e) => {
                                e.preventDefault();
                                const { contentSlug } = getVenueSlugs(venueDetails);
                                window.location.href = `/whats-nearby/${contentSlug}`;
                              }}
                              className="flex items-center justify-start gap-2 text-fg-brand-subtle hover:text-fg-brand-strong transition-colors focus-blue rounded-lg"
                            >
                              <span className="label-m-semibold">
                                {i18n.get("explorePlacesToVisitNear")} {venueDetails.name}
                              </span>
                              <ArrowRightIcon size={20} aria-hidden="true" />
                            </a>
                          </div>
                        </div>

                        {/* Desktop/Tablet Layout */}
                        <div className="hidden md:block">
                          <div className="relative flex">
                            <div className="absolute top-6 left-0 z-10">
                              <div className="w-[140px] lg:w-[180px] h-[140px] lg:h-[180px] rounded-lg overflow-hidden">
                                {isLoadingNearbyBusinesses ? (
                                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                    <div className="animate-pulse">
                                      <div className="w-full h-full bg-gray-300 rounded-lg"></div>
                                    </div>
                                  </div>
                                ) : (
                                  <img
                                    alt={`Map showing ${venueDetails.name} and ${nearbyBusinessesData?.data?.nearbyBusinessList?.length || 0} nearby venues`}
                                    className="w-full h-full object-cover"
                                    src={staticMapUrl}
                                    loading="lazy"
                                  />
                                )}
                              </div>
                            </div>
                            <div className="border border-[#deddf7] rounded-lg pl-28 lg:pl-36 pr-6 py-8 lg:py-12 ml-16 lg:ml-20 flex-1 min-h-[180px] lg:min-h-[228px] flex flex-col justify-center">
                              <h3 className="title-s-semibold lg:title-s-semibold text-fg-neutral-strong mb-3">
                                {i18n.get("nearbyVenues")}
                              </h3>
                              <p className="label-m-semibold text-fg-neutral-strong mb-4">
                                {i18n.get("fromDiningToLeisure")} {venueDetails.name} {i18n.get("nearbyVenuesDesc")}
                              </p>
                              <a
                                href={`/whats-nearby/${getVenueSlugs(venueDetails).contentSlug}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  const { contentSlug } = getVenueSlugs(venueDetails);
                                  window.location.href = `/whats-nearby/${contentSlug}`;
                                }}
                                className="flex items-center justify-start gap-2 text-fg-brand-subtle hover:text-fg-brand-strong transition-colors focus-blue rounded-lg"
                              >
                                <span className="label-m-semibold">
                                  {i18n.get("explorePlacesToVisitNear")} {venueDetails.name}
                                </span>
                                <ArrowRightIcon
                                  size={18}
                                  aria-hidden="true"
                                  className="lg:w-5 lg:h-5"
                                />
                              </a>
                            </div>
                          </div>
                        </div>
                      </section>
                    )}

                    {/* Others Section - Only show if there are business tabs */}
                    {othersCards.length > 0 && (
                      <>
                        <section id="others" aria-labelledby="others-heading" className="space-y-4 md:space-y-6">
                          <h2 id="others-heading" tabIndex={-1} className="title-s-semibold text-fg-neutral-strong tracking-[-0.11px] md:tracking-[-0.13px]">
                            {i18n.get("others")}
                          </h2>

                          {othersCards.length === 1 && (
                            <>
                              {othersCards.map((card) => (
                                <>
                                  {/* Mobile Layout */}
                                  <div className="md:hidden space-y-4">
                                    <div className="w-full h-[200px] rounded-lg overflow-hidden">
                                      <img
                                        alt={card.title}
                                        className="w-full h-full object-cover"
                                        src={card.image}
                                      />
                                    </div>
                                    <div className="border border-[#deddf7] rounded-lg p-6">
                                      <h3 className="title-s-semibold text-fg-neutral-strong mb-3">
                                        {card.title}
                                      </h3>

                                      {card.subtitle && (
                                        <p className="label-m-semibold text-fg-neutral-strong mb-4">
                                          {card.subtitle}
                                        </p>
                                      )}

                                      <a
                                        href={`/complexvenuetabs/${getVenueSlugs(venueDetails).contentSlug}/${card.id}`}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          const { contentSlug } = getVenueSlugs(venueDetails);
                                          window.location.href = `/complexvenuetabs/${contentSlug}/${card.id}`;
                                        }}
                                        className="flex items-center justify-start gap-2 text-fg-brand-subtle hover:text-fg-brand-strong transition-colors focus-blue rounded-lg mt-4"
                                      >
                                        <span className="label-m-semibold">
                                          {i18n.get("viewDetails")}
                                        </span>
                                        <ArrowRightIcon size={20} aria-hidden="true" />
                                      </a>
                                    </div>
                                  </div>

                                  {/* Desktop/Tablet Layout */}
                                  <div className="hidden md:block">
                                    <div
                                      key={card.id}
                                      className="relative flex"
                                    >
                                      <div className="absolute top-6 left-0 z-10">
                                        <div className="w-[140px] lg:w-[180px] h-[140px] lg:h-[180px] rounded-lg overflow-hidden">
                                          <img
                                            alt={card.title}
                                            className="w-full h-full object-cover"
                                            src={card.image}
                                          />
                                        </div>
                                      </div>
                                      <div className="border border-[#deddf7] rounded-lg pl-28 lg:pl-36 pr-6 py-8 lg:py-12 ml-16 lg:ml-20 flex-1 min-h-[180px] lg:min-h-[228px] flex flex-col justify-center">
                                        <h3 className="title-s-semibold lg:title-s-semibold text-fg-neutral-strong mb-3">
                                          {card.title}
                                        </h3>

                                        {card.subtitle && (
                                          <p className="label-m-medium text-fg-neutral-strong mb-4">
                                            {card.subtitle}
                                          </p>
                                        )}

                                        <a
                                          href={`/complexvenuetabs/${getVenueSlugs(venueDetails).contentSlug}/${card.id}`}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            const { contentSlug } = getVenueSlugs(venueDetails);
                                            window.location.href = `/complexvenuetabs/${contentSlug}/${card.id}`;
                                          }}
                                          className="flex items-center justify-start gap-2 text-fg-brand-subtle hover:text-fg-brand-strong transition-colors focus-blue rounded-lg"
                                        >
                                          <span className="label-m-semibold">
                                            {i18n.get("viewDetails")}
                                          </span>
                                          <ArrowRightIcon
                                            size={18}
                                            aria-hidden="true"
                                            className="lg:w-5 lg:h-5"
                                          />
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              ))}
                            </>
                          )}

                          {othersCards.length > 1 && (
                            <div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2">
                              {othersCards.map((card) => (
                                <div key={card.id} className="relative mt-8">
                                  {/* Image positioned to overflow outside the top of the container */}
                                  <div className="absolute top-[-20px] left-6 z-10">
                                    <div className="w-[100px] h-[100px] rounded-lg overflow-hidden">
                                      <img
                                        alt={card.title}
                                        className="w-full h-full object-cover"
                                        src={card.image}
                                      />
                                    </div>
                                  </div>

                                  {/* Card container */}
                                  <div className="border border-[#deddf7] rounded-lg px-4 pb-3 pt-10 relative">
                                    {/* Content positioned to the right of image */}
                                    <div className="ml-[120px] min-h-[50px] flex flex-col justify-between">
                                      <div>
                                        <h3 className="title-s-semibold text-fg-neutral-strong mb-1">
                                          {card.title}
                                        </h3>
                                        <p className="label-s-regular text-fg-neutral-strong">
                                          {card.subtitle}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Divider and link at bottom */}
                                    <div className="mt-4">
                                      <div className="h-[1px] bg-[#deddf7] mb-2"></div>
                                      <a
                                        href={`/complexvenuetabs/${getVenueSlugs(venueDetails).contentSlug}/${card.id}`}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          const { contentSlug } = getVenueSlugs(venueDetails);
                                          window.location.href = `/complexvenuetabs/${contentSlug}/${card.id}`;
                                        }}
                                        className="w-full flex items-center justify-center gap-2 text-fg-brand-subtle hover:text-fg-brand-strong transition-colors focus-blue rounded-lg"
                                      >
                                        <span className="label-s-medium">
                                          {i18n.get("viewDetails")}
                                        </span>
                                        <ArrowRightIcon size={16} aria-hidden="true" />
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </section>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </main>
        </>
      )}

      {/* Main Content */}

      {/* What's Nearby Section - Inline when no accessibility data */}
      {!hasAccessibilityData && hasNearbyVenues && (
        <>          
          <div className="bg-bg-brand-secondary-default mx-auto px-4 md:px-6 relative pb-8 md:pb-12">
            <div id="whatsnearby">
              {isLoadingNearbyBusinesses ? (
                <div className="flex justify-center py-8">
                  <Loader />
                </div>
              ) : (
                <Suspense
                  fallback={
                    <div className="flex justify-center py-8">
                      <Loader />
                    </div>
                  }
                >
                  <NearbyVenues
                    categories={
                      nearbyBusinessesData?.data?.nearbyBusinessCategories || []
                    }
                    nearbyBusinessList={
                      nearbyBusinessesData?.data?.nearbyBusinessList || []
                    }
                    venueLatitude={venueDetails.latitude}
                    venueLongitude={venueDetails.longitude}
                    venueId={venueDetails.business.id.toString()}
                    nearbyFilterAccessibility={
                      nearbyBusinessesData?.data?.filter_accessibility || []
                    }
                    accessibilityFeaturesRequirementsHiddenIds={
                      nearbyBusinessesData?.data?.accessibility_features_requirements_hidden_ids?.map(
                        String,
                      ) || []
                    }
                    accessibilityFeaturesRequirementsShowHiddenById={
                      nearbyBusinessesData?.data?.accessibility_features_requirements_show_hidden_by_id?.toString() ||
                      ""
                    }
                    hasAccessibilityData={false}
                    venueDetails={venueDetails as unknown as Venue}
                    businessPhotoFirst={
                      venueDetails.businessPhotoFirst?.path || ""
                    }
                    onGetAlert={handleGetAlert}
                    notificationVenueIds={notificationVenueIds}
                    isLoggedIn={isLoggedIn}
                    onShowAuthModal={() => showModal(AuthModalScreen.EnterEmail)}
                  />
                </Suspense>
              )}
            </div>
          </div>
        </>
      )}

      {openSavedPlacesModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={openSavedPlacesModal}
          onClose={() => handleSavedPlacesModalClose()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="saved-places-modal-title"
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <div id="saved-places-modal-title" className="sr-only">
            {savePlaceListMode === "create" ? "Create new list" : "Save to list"}
          </div>
          {savePlaceListMode === "create" && (
            <CreateList
              onClose={() => handleSavedPlacesModalClose()}
              businessId={venueDetails?.business.id}
              venueDetails={venueDetails}
              onCreateList={(listNameCreated) =>
                handleCreateList(listNameCreated)
              }
            />
          )}

          {savePlaceListMode === "list" && (
            <AddRemoveVenueToList
              onClickCreateNew={() => handleCreateNewClick()}
              onClose={() => handleSavedPlacesModalClose()}
              businessId={venueDetails?.business.id}
              venueDetails={venueDetails}
              listNameCreated={listNameCreated}
            />
          )}
        </Modal>
      )}

      {/* Notification Modal for nearby venues */}
      {selectedVenueForNotification && (
        <InterestNotificationModal
          businessId={selectedVenueForNotification.id}
          businessName={selectedVenueForNotification.name}
          brandId={selectedVenueForNotification.brandId}
          brandName={selectedVenueForNotification.brandName}
          interestId={selectedVenueForNotification.interestId}
          interestedType={selectedVenueForNotification.interestedType}
          notifyAccessibilityInformation={
            selectedVenueForNotification.notifyAccessibilityInformation
          }
          notifyLiveAlert={selectedVenueForNotification.notifyLiveAlert}
          isOpen={isNotificationModalOpen}
          onClose={handleNotificationModalClose}
          onSuccess={() => {
            // Called when the notification is successfully saved
            console.log("Notification preferences saved successfully");
          }}
        />
      )}
    </>
  );
}

export default ComplexVenueSheetContent;
